// src/context/TrackingContext.tsx
"use client";

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';

export type EmailEvent = {
  id?: string;
  eventType: 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'failed';
  emailId: string;
  emailType: 'invoice' | 'bill' | 'tax' | 'reminder';
  recipientEmail: string;
  timestamp: Date;
  metadata?: {
    subject?: string;
    ip?: string;
    userAgent?: string;
    clickUrl?: string;
    bounceReason?: string;
    errorMessage?: string;
    [key: string]: any;
  };
  documentId: string;
  budgetId: string;
  createdAt: Date;
};

export type TrackingStats = {
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  failed: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
};

type TrackingState = {
  events: EmailEvent[];
  stats: TrackingStats | null;
  loading: boolean;
  error: string | null;
};

type TrackingAction =
  | { type: 'SET_EVENTS'; payload: EmailEvent[] }
  | { type: 'ADD_EVENT'; payload: EmailEvent }
  | { type: 'SET_STATS'; payload: TrackingStats }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

const initialState: TrackingState = {
  events: [],
  stats: null,
  loading: false,
  error: null
};

const TrackingContext = createContext<{
  state: TrackingState;
  loadEvents: (budgetId: string) => Promise<void>;
  getTrackingStats: (budgetId: string) => Promise<void>;
} | undefined>(undefined);

function trackingReducer(state: TrackingState, action: TrackingAction): TrackingState {
  switch (action.type) {
    case 'SET_EVENTS':
      return { ...state, events: action.payload, loading: false };
    case 'ADD_EVENT':
      return { ...state, events: [...state.events, action.payload] };
    case 'SET_STATS':
      return { ...state, stats: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

export function TrackingProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(trackingReducer, initialState);

  const loadEvents = useCallback(async (budgetId: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const eventsQuery = query(
        collection(db, 'email_events'),
        where('budgetId', '==', budgetId),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(eventsQuery);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate(),
        createdAt: doc.data().createdAt.toDate()
      })) as EmailEvent[];

      dispatch({ type: 'SET_EVENTS', payload: events });
    } catch (error: any) {
      console.error('Error loading events:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const getTrackingStats = useCallback(async (budgetId: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    try {
      const eventsQuery = query(
        collection(db, 'email_events'),
        where('budgetId', '==', budgetId)
      );

      const snapshot = await getDocs(eventsQuery);
      const events = snapshot.docs.map(doc => doc.data() as EmailEvent);

      const totalEmails = events.length;
      const stats: TrackingStats = {
        delivered: events.filter(e => e.eventType === 'delivered').length,
        opened: events.filter(e => e.eventType === 'opened').length,
        clicked: events.filter(e => e.eventType === 'clicked').length,
        bounced: events.filter(e => e.eventType === 'bounced').length,
        complained: events.filter(e => e.eventType === 'complained').length,
        failed: events.filter(e => e.eventType === 'failed').length,
        deliveryRate: totalEmails ? events.filter(e => e.eventType === 'delivered').length / totalEmails : 0,
        openRate: totalEmails ? events.filter(e => e.eventType === 'opened').length / totalEmails : 0,
        clickRate: totalEmails ? events.filter(e => e.eventType === 'clicked').length / totalEmails : 0,
        bounceRate: totalEmails ? events.filter(e => e.eventType === 'bounced').length / totalEmails : 0
      };

      dispatch({ type: 'SET_STATS', payload: stats });
    } catch (error: any) {
      console.error('Error getting tracking stats:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  return (
    <TrackingContext.Provider value={{ state, loadEvents, getTrackingStats }}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTracking() {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return context;
}
